import React from 'react';
import clsx from 'clsx';

// Container component - for content width constraints
export const Container = ({ 
  children, 
  className = '', 
  size = 'md',
  as: Component = 'div',
  ...props 
}) => {
  const sizes = {
    sm: 'max-w-3xl',
    md: 'max-w-5xl',
    lg: 'max-w-7xl',
    xl: 'max-w-screen-2xl',
    full: 'max-w-full',
  };

  return (
    <Component
      className={clsx(
        'mx-auto px-4 sm:px-6 lg:px-8',
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Stack component - for vertical spacing
export const Stack = ({ 
  children, 
  className = '', 
  space = 'md',
  as: Component = 'div',
  divider = false,
  ...props 
}) => {
  const spaces = {
    xs: 'space-y-2',
    sm: 'space-y-4',
    md: 'space-y-6',
    lg: 'space-y-8',
    xl: 'space-y-12',
  };

  return (
    <Component
      className={clsx(
        'flex flex-col',
        spaces[space],
        divider && 'divide-y divide-gray-200 dark:divide-gray-800',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Grid component - for grid layouts
export const Grid = ({ 
  children, 
  className = '', 
  cols = { default: 1, sm: 2, md: 3, lg: 4 }, 
  gap = 'md',
  as: Component = 'div',
  ...props 
}) => {
  // Construct responsive column classes
  const colClasses = Object.entries(cols).map(([breakpoint, count]) => {
    if (breakpoint === 'default') {
      return `grid-cols-${count}`;
    }
    return `${breakpoint}:grid-cols-${count}`;
  }).join(' ');

  const gaps = {
    xs: 'gap-2',
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
    xl: 'gap-12',
  };

  return (
    <Component
      className={clsx(
        'grid',
        colClasses,
        gaps[gap],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Flex component - for flexbox layouts
export const Flex = ({ 
  children, 
  className = '', 
  direction = 'row', 
  wrap = false,
  justify = 'start',
  items = 'start',
  gap = 'md',
  as: Component = 'div',
  ...props 
}) => {
  const directions = {
    row: 'flex-row',
    'row-reverse': 'flex-row-reverse',
    col: 'flex-col',
    'col-reverse': 'flex-col-reverse',
  };

  const justifyOptions = {
    start: 'justify-start',
    end: 'justify-end',
    center: 'justify-center',
    between: 'justify-between',
    around: 'justify-around',
    evenly: 'justify-evenly',
  };

  const itemsOptions = {
    start: 'items-start',
    end: 'items-end',
    center: 'items-center',
    baseline: 'items-baseline',
    stretch: 'items-stretch',
  };

  const gaps = {
    xs: 'gap-2',
    sm: 'gap-4',
    md: 'gap-6',
    lg: 'gap-8',
    xl: 'gap-12',
  };

  return (
    <Component
      className={clsx(
        'flex',
        directions[direction],
        wrap && 'flex-wrap',
        justifyOptions[justify],
        itemsOptions[items],
        gaps[gap],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

// Simple horizontal divider
export const Divider = ({ 
  className = '', 
  ...props 
}) => {
  return (
    <hr
      className={clsx(
        'border-t border-gray-200 dark:border-gray-800',
        className
      )}
      {...props}
    />
  );
};
